import { useQuery } from '@tanstack/react-query'
import { portalApi } from '@lipaflow/portal-core'
import { homeName, longDate, type Tenancy } from './TenancyPicker'

/**
 * On the tenant's balance page (QA23-2): a home whose tenancy was called off
 * before they moved in is not their home. Says so above its invoices, and that
 * anything paid for it comes back, instead of only "You're up to date".
 * Filled into portal-core's `portalAccountStatus` slot.
 */
export function CalledOffNotice({ beneficiaryId }: { beneficiaryId?: string | null }) {
  const tenancies = useQuery({
    queryKey: ['portal-tenancies', 'all'],
    queryFn: async () => (await portalApi.get<{ items: Tenancy[] }>('/tenancy/list', { params: { page: 1, per_page: 100 } })).data,
    retry: false,
    enabled: !!beneficiaryId,
  })
  const onAccount = (tenancies.data?.items ?? []).filter((t) => t.account_id === beneficiaryId)
  // A current or upcoming tenancy on the same home is their home again.
  if (!onAccount.length || onAccount.some((t) => t.status !== 'terminated')) return null
  const latest = [...onAccount].sort((a, b) => b.started_on.localeCompare(a.started_on))[0]
  if (!latest.called_off_on) return null
  return <section role="status" className="rounded-2xl border border-warning/40 bg-warning-soft p-4 text-body-sm text-ink">
    <p className="font-semibold">Your tenancy of {homeName(latest)} was called off on {longDate(latest.called_off_on)}.</p>
    <p className="mt-1">It is not your home. Anything you paid for it is being refunded to you; you'll get a message when it is sent.</p>
  </section>
}
